<template>
  <div :class="$style.formContainer">
    <LoginCard header="Verify Your Identity">
      <p :class="$style.subheader">
        {{ mfaDisplayText.subheaderText }}
      </p>

      <Alert
        :message="errorMessage"
        :class="$style.alertMargin"
      />

      <form>
        <LoginActionButton
          :action="submit"
          :isLoading="isLoading"
          :disabled="isDisabled"
        >
          {{ mfaDisplayText.buttonText }}
        </LoginActionButton>

        <template v-if="isDisabled">
          <span :class="$style.requestSent">
            <InlineCheckmark :class="$style.checkmark" />
            Request Sent. Approve it on your phone.
          </span>
        </template>
      </form>

      <ChooseMfa
        v-bind="$props"
        :selectedFactor="factor"
      />
    </LoginCard>
  </div>
</template>

<script>
// Frameworks
import { get, isEmpty } from 'lodash';

// Constants and Types
import { LoginConsts } from 'client/util/Constants';
import { Push } from 'client/containers/Login/MfaType';

// Routes
import { UserRoutes } from 'client/routes/RouteGenerator';

// Components
import ChooseMfa from 'client/containers/Login/ChooseMfa.vue';
import InlineCheckmark from 'public/img/icons/inline-checkmark.svg?inline';
import LoginActionButton from 'client/components/LoginActionButton.vue';
import LoginCard from 'client/components/LoginCard.vue';

// API
import PushMfaAuthApi, { PushAuthenticationStatus } from 'client/api/PushMfaAuthApi';
import api from 'client/api';

// Utils
import loginUtil from 'client/components/mixins/loginUtil';
import poll from 'client/containers/Login/poll';

const {
  errorParamTypes,
  successParamTypes,
  pushStatusMessages,
  responseMessages,
} = LoginConsts;
const { resetPasswordUserForm } = UserRoutes;

export default {
  name: 'VerifyMobilePush',

  components: {
    ChooseMfa,
    InlineCheckmark,
    LoginActionButton,
    LoginCard,
  },

  data() {
    return {
      isDisabled: false,
    };
  },

  props: {
    factor: {
      type: Push,
      default: () => new Push(),
    },
    factors: {
      type: Array,
      default: () => ([]),
    },
    context: {
      type: String,
      default: '',
    },
    mfaDisplayText: {
      type: Object,
      default: () => ({}),
    },
    redirectTo: {
      type: String,
      default: LoginConsts.userConsoleRootRoute,
    },
    passwordResetInfo: {
      type: Object,
      default: null,
    },
    shouldPromptImmediately: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [loginUtil],

  api,
  pushApi: PushMfaAuthApi,

  pollConfig: { delay: 1000 },

  mounted() {
    if (this.shouldPromptImmediately) {
      this.submit();
    }
  },

  methods: {
    poll(pushId = '') {
      poll(
        async () => this.$options.pushApi
          .getPushAuthentication(pushId)
          .catch((err) => ({
            status: err.response.status,
            data: err.response.data,
          })),
        {
          successCondition: this.pollSuccessCondition,
          successHandler: this.pollSuccessHandler.bind(this, pushId),
          failureCondition: this.pollFailureCondition,
          failureHandler: this.pollFailureHandler.bind(this, pushId),
        },
        this.$options.pollConfig,
      );
    },

    pollSuccessCondition(response) {
      return response.status === PushAuthenticationStatus.ACCEPTED;
    },

    async pollSuccessHandler(pushId) {
      try {
        await this.$options.pushApi.finishPushAuthentication(pushId);

        if (this.context === LoginConsts.sso) {
          return this.redirect(UserRoutes.ssoRedirectUrl(this.redirectTo));
        }

        if (this.context === LoginConsts.oauth) {
          return this.redirect(UserRoutes.oauthRedirectUrl(this.redirectTo));
        }

        return this.redirect(this.redirectTo);
      } catch (error) {
        this.isDisabled = false;
        this.isLoading = false;
        return this.handleError(error && error.message);
      }
    },

    pollFailureCondition(response) {
      const { status } = response;

      return (status === undefined)
      || ((status !== PushAuthenticationStatus.ACCEPTED)
      && (status !== PushAuthenticationStatus.PENDING));
    },

    async pollFailureHandler(pushId, responseGetPushAuthentication) {
      const { status } = responseGetPushAuthentication;

      // 404 means that it is really really old
      pushStatusMessages['404'] = pushStatusMessages.expired;

      if (status === PushAuthenticationStatus.DENIED) {
        try {
          await this.$options.pushApi.finishPushAuthentication(pushId);
        } catch (e) {
          // Failure is expected and not an error
        }
        this.redirect(`/login?error=${errorParamTypes.pushDenied}`);
        return;
      }

      this.isDisabled = false;
      this.isLoading = false;

      const message = pushStatusMessages[status];
      this.handleError(message);
    },

    handleError(message = pushStatusMessages.errored) {
      this.errorMessage = message;
    },

    passwordResetFailureHandler(response) {
      const statusCode = get(response, 'statusCode');
      const pushStatus = get(response, 'status', PushAuthenticationStatus.ERRORED);

      // 400 response indicates either mfa has expired or password doesnt meet org reqs
      if (statusCode === 400) {
        const message = get(response, 'message', responseMessages.passwordResetError);
        const confirmationCode = get(this.passwordResetInfo, 'confirmationCode');
        this.redirect(`${resetPasswordUserForm}&key=${confirmationCode}&error=${message}`);
        return;
      }

      if (pushStatus === PushAuthenticationStatus.DENIED) {
        this.redirect(`/login?error=${errorParamTypes.pwdResetDenied}`);
      } else {
        this.isDisabled = false;
        this.isLoading = false;

        const message = pushStatusMessages[pushStatus];

        this.handleError(message);
      }
    },

    resetPasswordRequest() {
      this.isDisabled = true;

      poll(
        () => this.$options.api[this.clientType].resetPassword(
          this.passwordResetInfo.confirmationCode,
          {
            ...this.passwordResetInfo,
            push: true,
          },
        )
          .then((response) => response.data)
          .catch((error) => {
            const errorResponseData = get(error, 'response.data', {});
            errorResponseData.statusCode = get(error, 'response.status');

            return errorResponseData;
          }),
        {
          successCondition: this.pollSuccessCondition,
          successHandler: () => {
            this.redirect(`${this.redirectTo}?success=${successParamTypes.pwdResetSuccess}`);
          },
          failureCondition: this.pollFailureCondition,
          failureHandler: this.passwordResetFailureHandler,
        },
        this.$options.pollConfig,
      );
    },

    async authenticationRequest() {
      this.isDisabled = true;

      try {
        const response = await this.$options.pushApi.startPushAuthentication();

        return await this.poll(response.id);
      } catch (error) {
        this.isLoading = false;
        this.isDisabled = false;

        return this.handleError();
      }
    },

    async request() {
      const requestMethod = isEmpty(this.passwordResetInfo)
        ? this.authenticationRequest
        : this.resetPasswordRequest;

      requestMethod();
    },
  },
};
</script>

<style module>
@import '../css/login-components.css';

@value (jcGrey800) from 'css/brand-colors.css';

.subheader {
  font-size: 0.75rem;
  font-weight: 600;
}

.requestSent {
  align-items: center;
  color: jcGrey800;
  display: flex;
  font-size: 0.75rem;
  font-weight: 400;
  justify-content: center;
  margin: -1rem 0 2rem 0;
}

.checkmark {
  display: inline;
  height: 0.75rem;
  margin-right: 0.5rem;
  width: 1rem;
}

button[disabled=disabled] {
  cursor: not-allowed;
}
</style>
